export const PUBLIC_SITEMAP_MAX_URLS = 50_000;
function escapeXml(value) {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
}
function normalizeSiteUrl(siteUrl) {
    return siteUrl.replace(/\/$/, "");
}
export function buildSitemapXml(site, siteUrl) {
    return buildSitemapXmlFromEntries(site.getSitemapEntries(), siteUrl);
}
export function buildSitemapXmlFromEntries(entries, siteUrl) {
    const normalizedSiteUrl = normalizeSiteUrl(siteUrl);
    const urls = entries.map((entry) => [
        "    <url>",
        `        <loc>${escapeXml(`${normalizedSiteUrl}${entry.path}`)}</loc>`,
        `        <lastmod>${escapeXml(entry.lastmod)}</lastmod>`,
        `        <changefreq>${escapeXml(entry.changefreq)}</changefreq>`,
        `        <priority>${entry.priority.toFixed(1)}</priority>`,
        "    </url>",
    ].join("\n"));
    return [
        "<?xml version=\"1.0\" encoding=\"UTF-8\"?>",
        "<urlset xmlns=\"http://www.sitemaps.org/schemas/sitemap/0.9\">",
        ...urls,
        "</urlset>",
        "",
    ].join("\n");
}
export function buildSitemapIndexXml(sitemapUrls) {
    const sitemaps = sitemapUrls.map((sitemap) => [
        "    <sitemap>",
        `        <loc>${escapeXml(sitemap.url)}</loc>`,
        ...(sitemap.lastmod ? [`        <lastmod>${escapeXml(sitemap.lastmod)}</lastmod>`] : []),
        "    </sitemap>",
    ].join("\n"));
    return [
        "<?xml version=\"1.0\" encoding=\"UTF-8\"?>",
        "<sitemapindex xmlns=\"http://www.sitemaps.org/schemas/sitemap/0.9\">",
        ...sitemaps,
        "</sitemapindex>",
        "",
    ].join("\n");
}
function paginate(items, pageSize) {
    const pages = [];
    for (let offset = 0; offset < items.length; offset += pageSize)
        pages.push(items.slice(offset, offset + pageSize));
    return pages;
}
export function buildStaticSitemapDocuments(site, siteUrl, maximumUrls = PUBLIC_SITEMAP_MAX_URLS) {
    if (!Number.isSafeInteger(maximumUrls) || maximumUrls < 1)
        throw new Error("maximumUrls must be a positive safe integer.");
    const pages = paginate(site.getSitemapEntries(), maximumUrls);
    const multiple = pages.length > 1;
    return pages.map((entries, index) => ({
        name: multiple ? `static-${index + 1}` : "static",
        xml: buildSitemapXmlFromEntries(entries, siteUrl),
    }));
}
export function definePublicSitemapSource(source) {
    return source;
}
export function definePublicSitemapSources(sources) {
    return sources;
}
function getLocalizedVariantCount(route, localeCount) {
    if (route.localePolicy === "none")
        return 1;
    return localeCount + (route.localePolicy.unlocalized === "indexable" ? 1 : 0);
}
function normalizeBasePath(basePath) {
    const normalized = `/${basePath}`.replace(/\/+/g, "/").replace(/\/$/, "");
    return normalized || "/sitemaps";
}
function assertCollectionSourceName(source) {
    if (!/^[a-z0-9][a-z0-9-]*$/.test(source))
        throw new Error(`Invalid sitemap collection source '${source}'.`);
}
function assertCollectionCount(source, count) {
    if (!Number.isSafeInteger(count) || count < 0)
        throw new Error(`Sitemap source '${source}' returned an invalid count.`);
}
function normalizeLastModified(value) {
    return value instanceof Date
        ? value.toISOString()
        : value;
}
export function createPublicMultiSitemap(options) {
    const siteUrl = normalizeSiteUrl(options.siteUrl);
    const basePath = normalizeBasePath(options.basePath ?? "/sitemaps");
    const maximumUrls = options.maximumUrls ?? PUBLIC_SITEMAP_MAX_URLS;
    const collections = options.site.getSitemapCollections();
    const collectionBySource = new Map(collections.map((collection) => [
        collection.source,
        collection,
    ]));
    if (!Number.isSafeInteger(maximumUrls) || maximumUrls < 1)
        throw new Error("maximumUrls must be a positive safe integer.");
    for (const collection of collections) {
        assertCollectionSourceName(collection.source);
        if (!options.allowMissingSources && !options.sources[collection.source])
            throw new Error(`Missing sitemap source '${collection.source}'.`);
    }
    for (const source of Object.keys(options.sources))
        if (!collectionBySource.has(source))
            throw new Error(`Sitemap source '${source}' is not declared by the public site manifest.`);
    const staticDocuments = buildStaticSitemapDocuments(options.site, siteUrl, maximumUrls);
    const getDocuments = async () => {
        const dynamicDocuments = await Promise.all(collections.map(async (collection) => {
            const source = options.sources[collection.source];
            if (!source)
                return [];
            const count = await source.count();
            assertCollectionCount(collection.source, count);
            const variants = getLocalizedVariantCount(collection, options.site.locales.length);
            const pageSize = Math.max(1, Math.floor(maximumUrls / variants));
            const pageCount = Math.ceil(count / pageSize);
            return Array.from({ length: pageCount }, (_, index) => ({
                kind: "collection",
                name: `${collection.source}-${index + 1}`,
                url: `${siteUrl}${basePath}/${collection.source}-${index + 1}.xml`,
                source: collection.source,
                page: index + 1,
                pageSize,
            }));
        }));
        const documents = [
            ...staticDocuments.map((document) => ({
                kind: "static",
                name: document.name,
                url: `${siteUrl}${basePath}/${document.name}.xml`,
            })),
            ...dynamicDocuments.flat(),
        ];
        if (documents.length > PUBLIC_SITEMAP_MAX_URLS)
            throw new Error(`Sitemap index exceeds ${PUBLIC_SITEMAP_MAX_URLS} documents.`);
        return documents;
    };
    const renderIndex = async () => {
        const documents = await getDocuments();
        return buildSitemapIndexXml(documents.map((document) => ({
            url: document.url,
        })));
    };
    const renderDocument = async (name) => {
        const staticDocument = staticDocuments.find((document) => document.name === name);
        if (staticDocument)
            return staticDocument.xml;
        const nameMatch = /^(.*)-(\d+)$/.exec(name);
        const collection = nameMatch?.[1]
            ? collectionBySource.get(nameMatch[1])
            : undefined;
        if (!collection)
            return null;
        const rawPage = nameMatch?.[2] ?? "";
        const page = /^\d+$/.test(rawPage) ? Number(rawPage) : 0;
        const source = options.sources[collection.source];
        if (!Number.isSafeInteger(page) || page < 1 || !source)
            return null;
        const count = await source.count();
        assertCollectionCount(collection.source, count);
        const variants = getLocalizedVariantCount(collection, options.site.locales.length);
        const pageSize = Math.max(1, Math.floor(maximumUrls / variants));
        const pageCount = Math.ceil(count / pageSize);
        if (page > pageCount)
            return null;
        const items = await source.getPage({
            page,
            pageSize,
        });
        if (items.length === 0)
            return null;
        const entryByPath = new Map();
        for (const item of items) {
            const logicalPath = source.getPath(item);
            const match = options.site.matchRoute(logicalPath);
            if (!match || match.route.key !== collection.key)
                throw new Error(`Sitemap source '${collection.source}' returned path '${logicalPath}' outside route '${collection.pathPattern}'.`);
            const lastmod = normalizeLastModified(source.getLastModified(item));
            const changefreq = source.getChangeFrequency?.(item) ?? collection.changefreq;
            const priority = source.getPriority?.(item) ?? collection.priority;
            if (!Number.isFinite(priority) || priority < 0 || priority > 1)
                throw new Error(`Sitemap source '${collection.source}' returned invalid priority '${priority}'.`);
            for (const path of options.site.getIndexablePaths(logicalPath))
                entryByPath.set(path, {
                    path,
                    lastmod,
                    changefreq,
                    priority,
                });
        }
        const entries = [...entryByPath.values()];
        if (entries.length > maximumUrls)
            throw new Error(`Sitemap document '${name}' exceeds ${maximumUrls} URLs.`);
        return buildSitemapXmlFromEntries(entries, siteUrl);
    };
    return {
        getDocuments,
        renderIndex,
        renderDocument,
    };
}
//# sourceMappingURL=index.js.map